import { useSyncExternalStore } from "react";
import { setLocale } from "@/locales/i18n";
import type { AppLocale } from "@/locales/types";
import { EMBEDDED_THEME_TOKEN_PROPERTIES, type EmbeddedTheme, type EmbeddedThemeToken } from "./protocol";

/**
 * 嵌入端外观完全受宿主控制，不读写主题或语言存储，也不跟随系统模式。
 * 宿主未推送时使用浅色默认配色；每次更新都是完整快照，未提供的 token 回到模式默认值。
 */
export const EMBEDDED_DEFAULT_THEME: EmbeddedTheme = Object.freeze({ mode: "light" });

type AppearanceState = { theme: EmbeddedTheme };

let state: AppearanceState = { theme: EMBEDDED_DEFAULT_THEME };
let appearanceSeq = 0;
const listeners = new Set<() => void>();
const tokenProperties = Object.entries(EMBEDDED_THEME_TOKEN_PROPERTIES) as [EmbeddedThemeToken, string][];

function applyThemeToDocument(theme: EmbeddedTheme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme.mode === "dark");
  // index.html 的防闪屏脚本写了 important，这里使用相同优先级覆盖。
  root.style.setProperty("color-scheme", theme.mode, "important");
  for (const [token, property] of tokenProperties) {
    const value = theme.tokens?.[token];
    if (value === undefined) {
      root.style.removeProperty(property);
    } else {
      root.style.setProperty(property, value);
    }
  }
}

function themesEqual(left: EmbeddedTheme, right: EmbeddedTheme): boolean {
  return left.mode === right.mode && tokenProperties.every(([token]) => left.tokens?.[token] === right.tokens?.[token]);
}

/** 渲染 React 前恢复嵌入端默认外观，不沿用独立工作台的存储偏好。 */
export function prepareEmbeddedAppearance(): void {
  applyThemeToDocument(state.theme);
}

/** 宿主推送的完整外观快照，seq 与 token / context 各自独立递增。 */
export function applyEmbeddedAppearance(seq: number, locale: AppLocale, theme: EmbeddedTheme): boolean {
  if (seq <= appearanceSeq) {
    return false;
  }

  appearanceSeq = seq;
  setLocale(locale);

  if (!themesEqual(state.theme, theme)) {
    // 保留自己的快照，宿主后续修改对象时不能绕过 seq 更新当前主题。
    const snapshot: EmbeddedTheme = { mode: theme.mode, ...(theme.tokens ? { tokens: { ...theme.tokens } } : {}) };
    state = { theme: snapshot };
    applyThemeToDocument(snapshot);
    listeners.forEach((listener) => {
      listener();
    });
  }
  return true;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): AppearanceState {
  return state;
}

/** 主题边界位于 router 之上，消息接收位于其内，因此通过外部 store 同步。 */
export function useEmbeddedTheme(): EmbeddedTheme {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot).theme;
}
